import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import Navigation from '../home/components/Navigation';
import ParticleEffect from '../home/components/ParticleEffect';
import VideoUpload from '../../components/feature/VideoUpload';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface CollectiveQuestion {
  id: string;
  question: string;
  answer: string | null;
  created_at: string;
}

interface CollectiveVideo {
  id: string;
  title: string;
  description: string;
  video_url: string;
  created_at: string;
}

export default function AskCollectivePage() {
  const [questions, setQuestions] = useState<CollectiveQuestion[]>([]);
  const [videos, setVideos] = useState<CollectiveVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    loadQuestions();
    loadVideos();
  }, []);

  const loadQuestions = async () => {
    try {
      const { data, error } = await supabase
        .from('collective_questions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading questions:', error);
        setQuestions([]);
      } else {
        setQuestions(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
      setQuestions([]);
    }
  };

  const loadVideos = async () => {
    try {
      const { data, error } = await supabase
        .from('collective_videos')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading videos:', error);
        setVideos([]);
      } else {
        setVideos(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoUploadComplete = (newVideo: CollectiveVideo) => {
    setVideos(prev => [newVideo, ...prev]);
    setShowUpload(false);
  };

  // Filter questions to only show those with answers
  const answeredQuestions = questions.filter(q => q.answer);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white relative overflow-hidden">
        <ParticleEffect />
        <Navigation />
        <div className="pt-24 pb-16 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-xl md:text-2xl font-light tracking-wide">
              Connecting to the collective consciousness...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <ParticleEffect />
      <Navigation />
      
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-light tracking-wider mb-6 text-transparent bg-gradient-to-r from-violet-400 to-cyan-300 bg-clip-text">
              Ask the Collective
            </h1>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-violet-400 to-transparent mx-auto mb-8"></div>
            <p className="text-xl text-purple-200 max-w-2xl mx-auto leading-relaxed">
              Direct responses from the collective consciousness. To submit your questions, please visit our <a href="/contact" className="text-violet-400 hover:text-violet-300 underline cursor-pointer">Contact page</a>.
            </p>
          </div>

          {/* Collective Video Responses */}
          {videos.length > 0 && (
            <div className="mb-16">
              <h2 className="text-3xl font-light tracking-wide mb-8 text-center text-transparent bg-gradient-to-r from-violet-400 to-cyan-300 bg-clip-text">
                Video Responses from the Collective
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {videos.map((video) => (
                  <div key={video.id} className="bg-black/60 backdrop-blur-sm border border-purple-500/30 rounded-lg overflow-hidden hover:border-purple-400/50 transition-all duration-300">
                    <div className="aspect-video bg-gradient-to-br from-purple-900/40 to-blue-900/40">
                      <video
                        src={video.video_url}
                        controls
                        className="w-full h-full object-contain"
                        preload="metadata"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-white mb-2">{video.title}</h3>
                      <p className="text-purple-300 text-sm leading-relaxed mb-4">{video.description}</p>
                      <div className="text-xs text-purple-400">
                        {new Date(video.created_at).toLocaleDateString()} at {new Date(video.created_at).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Text-based Answered Questions */}
          {answeredQuestions.length > 0 && (
            <div>
              <h2 className="text-3xl font-light tracking-wide mb-8 text-center text-transparent bg-gradient-to-r from-violet-400 to-cyan-300 bg-clip-text">
                Collective Wisdom
              </h2>
              
              <div className="space-y-8">
                {answeredQuestions.map((item) => (
                  <div key={item.id} className="bg-black/60 backdrop-blur-sm border border-purple-500/30 rounded-lg p-6 hover:border-purple-400/50 transition-all duration-300">
                    <div className="mb-4">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-8 h-8 bg-blue-600/30 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-question-line text-blue-300 text-sm"></i>
                        </div>
                        <p className="text-blue-200 font-medium">{item.question}</p>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-purple-600/30 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-brain-line text-purple-300 text-sm"></i>
                        </div>
                        <p className="text-purple-100 leading-relaxed">{item.answer}</p>
                      </div>
                    </div>
                    
                    <div className="text-xs text-purple-400 mt-4">
                      {new Date(item.created_at).toLocaleDateString()} at {new Date(item.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {answeredQuestions.length === 0 && videos.length === 0 && (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-full flex items-center justify-center">
                <i className="ri-question-line text-3xl text-purple-300"></i>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-purple-200">
                The Collective Awaits
              </h3>
              <p className="text-purple-300 max-w-md mx-auto">
                Submit your questions through our <a href="/contact" className="text-violet-400 hover:text-violet-300 underline cursor-pointer">Contact page</a>. The collective consciousness will respond with wisdom and insight.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
